/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import { FormContainer, TextFieldElement } from "react-hook-form-mui";
import { useForm } from "react-hook-form";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import ButtonLoading from "../../Button/ButtonLoading";
import { apiGet, apiPost } from "../../../services/Api/apiCall";
import { ResponseError } from "../../../services/ApiClient";
import { isEmailDefaultFieldBased, requiredFieldDefaultFieldBased } from "../../../services/reactHookFormValidationRules";
import { ProductUnion } from "../../../types/ProductUnion";
import { HydraCollectionResult } from "../../../types/HydraCollectionResult";
import { Customer } from "../../../types/Customer";

type ReassignCaseModalProps = {
  product: ProductUnion;
  open: boolean;
  handleClose: Function;
  refreshPage?: Function;
};

type FormValues = {
  email: string;
};

type ReassignCaseResponse = {
  changedEntitiesCount?: number;
  message?: string;
};

const REASSIGN_CASE_COMMAND_PATH = "/lb/services/change_customer_of_case";

export default function ReassignCaseModal({ product, open, handleClose, refreshPage = () => {} }: ReassignCaseModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const formContext = useForm<FormValues>({
    defaultValues: {
      email: "",
    },
  });

  const closeModal = () => {
    formContext.reset();
    setErrorMessage("");
    handleClose();
  };

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const normalizedEmail = values.email.trim().toLowerCase();
      const customersByEmail: HydraCollectionResult<Customer> = await apiGet("customers", null, {
        email: normalizedEmail,
      });
      const newCustomer = customersByEmail["hydra:member"]?.[0];
      if (!newCustomer) {
        setErrorMessage(`Es wurde kein Kundenkonto mit der E-Mail-Adresse ${normalizedEmail} gefunden.`);
        return;
      }

      const response: ReassignCaseResponse = await apiPost(REASSIGN_CASE_COMMAND_PATH, {
        caseId: product.id,
        newCustomerId: newCustomer.id,
      });
      const changedEntitiesCount =
        typeof response?.changedEntitiesCount === "number" ? response.changedEntitiesCount : null;
      const backendMessage = typeof response?.message === "string" ? response.message : "";

      if (changedEntitiesCount === 0) {
        setErrorMessage(
          backendMessage || "Die ausgewählte Mandantschaft ist bereits dem Fall zugeordnet. Keine Neuzuweisung nötig.",
        );
        return;
      }

      enqueueSnackbar(
        backendMessage ||
          (changedEntitiesCount !== null
            ? `Fall erfolgreich neu zugewiesen (${changedEntitiesCount} Entitäten aktualisiert)`
            : "Fall erfolgreich neu zugewiesen"),
        {
          variant: "custom",
          isNonInteractive: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        },
      );
      refreshPage();
      closeModal();
    } catch (error) {
      setErrorMessage(
        error instanceof ResponseError ? error.message : "Die Fallzuweisung konnte nicht geändert werden.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LegalbirdIoModal
      handleClose={closeModal}
      open={open}
      title={"Fall neu zuweisen"}
      cancelButtonLabel={"Abbrechen"}
      submitButton={
        <FormContainer formContext={formContext} onSuccess={onSubmit}>
          <ButtonLoading variant={"contained"} color={"primary"} type={"submit"} isLoading={isLoading}>
            Fall neu zuweisen
          </ButtonLoading>
        </FormContainer>
      }
    >
      <FormContainer formContext={formContext} onSuccess={onSubmit}>
        <Typography sx={{ mb: 2 }}>
          Um den Fall einem bereits bestehenden Kundenkonto neu zuzuweisen, geben Sie bitte die E-Mail-Adresse des
          bestehenden Kontos ein und klicken Sie anschließend auf &ldquo;Fall neu zuweisen&rdquo;.
        </Typography>
        <TextFieldElement
          label={"E-Mail-Adresse des bestehenden Kundenkontos"}
          name={"email"}
          type={"email"}
          fullWidth
          rules={{ ...requiredFieldDefaultFieldBased, ...isEmailDefaultFieldBased }}
          disabled={isLoading}
        />
        {errorMessage && (
          <Typography sx={{ color: "red", mt: 2 }} align={"center"}>
            {errorMessage}
          </Typography>
        )}
      </FormContainer>
    </LegalbirdIoModal>
  );
}
